import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  CurrencyRupee as CurrencyRupeeIcon,
  Description as DescriptionIcon,
  Leaderboard as LeaderboardIcon,
  LocationOn as LocationOnIcon,
  Paid as PaidIcon,
  Storefront as StorefrontIcon,
  NoteAddOutlined as NoteAddOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
  CancelOutlined as CancelOutlinedIcon,
  ThumbUpAltOutlined as ThumbUpAltOutlinedIcon,
  CalendarMonth as CalendarMonthIcon,
  Close,
} from "@mui/icons-material";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { API_URL } from "app/config";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import AnalyticsCard from "./AnalyticsCard";
import AnalyticsTimeSeries from "./AnalyticsTimeSeries";

const BankingSection = ({
  analyticsData,
  getAllAnalyticsData,
  mainStateName,
}) => {
  const banking = analyticsData?.data?.banking;
  const bankingSummary = banking?.summary;
  const topBankingStates = banking?.top_states;
  const topBankingAgents = banking?.top_agents;
  const topLoanProducts = banking?.top_products;
  const topBankingDistricts = banking?.top_districts || [];
  const [selectedBankingState, setSelectedBankingState] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStateDistricts, setSelectedStateDistricts] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleStateClick = async (stateName) => {
    setSelectedBankingState(stateName);
    setOpenDialog(true);
    setSelectedStateDistricts([]);
    setLoading(true);

    try {
      const response = await fetch(
        `${API_URL}/mis/api/v1/mis-analytics?use_cache=true`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ state_name: stateName }),
        }
      );

      const data = await response.json();

      const districts = data?.data?.banking?.top_districts;

      setSelectedStateDistricts(districts);
    } catch (error) {
      console.error("Error fetching district data:", error);
      setSelectedStateDistricts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mainStateName) {
      setSelectedBankingState(mainStateName);
    } else {
      setSelectedBankingState(null);
    }
  }, [mainStateName]);

  useEffect(() => {
    if (mainStateName) {
      getAllAnalyticsData({
        state_name: mainStateName,
      });
    }
  }, [mainStateName, getAllAnalyticsData]);

  return (
    <Box>
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Leads Generated"
            count={
              (bankingSummary?.pending_applications || 0) +
              (bankingSummary?.completed_applications || 0) +
              (bankingSummary?.approved_applications || 0) +
              (bankingSummary?.rejected_applications || 0) +
              (bankingSummary?.sanctioned_applications || 0) +
              (bankingSummary?.disbursed_applications || 0)
            }
            icon={DescriptionIcon}
            color="#0ea5e9"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Leads Generated (Last 6 Months)"
            count={bankingSummary?.total_leads_generated_last_6_months}
            icon={CalendarMonthIcon}
            color="#f59e0b"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Applications Pending"
            count={bankingSummary?.pending_applications}
            icon={NoteAddOutlinedIcon}
            color="#6366f1"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Applications Completed"
            count={bankingSummary?.completed_applications}
            icon={CheckCircleOutlineIcon}
            color="#8b5cf6"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Applications Approved"
            count={bankingSummary?.approved_applications}
            icon={TaskAltOutlinedIcon}
            color="#22c55e"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Applications Rejected"
            count={bankingSummary?.rejected_applications}
            icon={CancelOutlinedIcon}
            color="#ef4444"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Applications Sanctioned"
            count={bankingSummary?.sanctioned_applications}
            icon={ThumbUpAltOutlinedIcon}
            color="#14b8a6"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Applications Disbursed"
            count={bankingSummary?.disbursed_applications}
            icon={PaidIcon}
            color="#0ea5e9"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Loan Amount Requested"
            count={bankingSummary?.total_loan_amount_requested}
            icon={CurrencyRupeeIcon}
            color="#3b82f6"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Loan Amount Approved"
            count={bankingSummary?.total_loan_amount_approved}
            icon={CurrencyRupeeIcon}
            color="#a855f7"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Loan Amount Sanctioned"
            count={bankingSummary?.total_loan_amount_sanctioned}
            icon={CurrencyRupeeIcon}
            color="#06b6d4"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <AnalyticsCard
            title="Loan Amount Disbursed"
            count={bankingSummary?.total_loan_amount_disbursed}
            icon={CurrencyRupeeIcon}
            color="#16a34a"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} marginBottom={4}>
        <Grid item xs={12} sm={6} md={4}>
          <AnalyticsCard
            title={
              mainStateName
                ? `Districts in ${mainStateName}`
                : "Top Banking States"
            }
            icon={LocationOnIcon}
            color="#14b8a6"
            sx={{ height: "100%", position: "relative" }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: -2,
                mb: -3.5,
              }}
            >
              {mainStateName
                ? topBankingDistricts?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        borderBottom:
                          index !== topBankingDistricts.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                      >
                        {item.district_name || "Other"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#0ea5e9",
                          textAlign: "right",
                        }}
                      >
                        {item.total_count}
                      </Typography>
                    </Box>
                  ))
                : topBankingStates?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        py: 1,
                        borderBottom:
                          index !== topBankingStates.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                        cursor: !mainStateName ? "pointer" : "default",
                        "&:hover": !mainStateName
                          ? { background: "#f9fafb" }
                          : {},
                      }}
                      onClick={() => handleStateClick(item.state_name)}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "#333",
                          width: "80%",
                        }}
                      >
                        {mainStateName
                          ? item.district_name
                          : item.state_name || "Other"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#14b8a6",
                          textAlign: "right",
                        }}
                      >
                        {item.total_count}
                      </Typography>
                    </Box>
                  ))}
            </Box>
          </AnalyticsCard>
        </Grid>

        {/* Top Banking Agents */}
        <Grid item xs={12} sm={6} md={4}>
          <AnalyticsCard
            title="Top Banking Agents"
            icon={LeaderboardIcon}
            color="#22c55e"
            sx={{ height: "100%" }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: -2,
                mb: -3.5,
              }}
            >
              {topBankingAgents?.map((agent, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom:
                      index !== topBankingAgents.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                  >
                    {agent.agent_name || "Other"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      // color: "#0ea5e9",
                      color: "#14b8a6",
                      textAlign: "right",
                    }}
                  >
                    {agent.total_count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AnalyticsCard>
        </Grid>

        {/*  Top Loan Products */}
        <Grid item xs={12} sm={6} md={4}>
          <AnalyticsCard
            title="Top Loan Products"
            icon={StorefrontIcon}
            color="#6366f1"
            sx={{ height: "100%" }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mt: -2,
                mb: -3.5,
              }}
            >
              {topLoanProducts?.map((product, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom:
                      index !== topLoanProducts.length - 1
                        ? "1px solid #f0f0f0"
                        : "none",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                  >
                    {product.product_name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#6366f1",
                      textAlign: "right",
                    }}
                  >
                    {product.total_count}
                  </Typography>
                </Box>
              ))}
            </Box>
          </AnalyticsCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12}>
          <AnalyticsTimeSeries
            TimeSeries={banking?.timeseries}
            title="Banking Trend"
          />
        </Grid>
      </Grid>

      <Modal
        show={openDialog}
        onHide={() => setOpenDialog(false)}
        size="md"
        className="analytics-modal "
      >
        <AnalyticsCard
          title={` Districts in ${selectedBankingState}`}
          color="#f59e0b"
          sx={{
            marginBottom: 0,
            "&:hover": {
              transform: "translateY(0px)",
            },
          }}
        >
          <Close
            style={{
              position: "absolute",
              top: 10,
              right: 16,
              fontSize: 22,
              opacity: 0.8,
              color: "#f59e0b",
            }}
            onClick={() => setOpenDialog(false)}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "100px",
                }}
              >
                <CircularProgress />
              </Box>
            ) : selectedStateDistricts?.length > 0 ? (
              selectedStateDistricts.map((item, index) => (
                <>
                  <Box
                    key={index}
                    className="px-2"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom:
                        index !== selectedStateDistricts.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#333", width: "80%" }}
                    >
                      {item.district_name || "Other"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#0ea5e9",
                        textAlign: "right",
                      }}
                    >
                      {item.total_count}
                    </Typography>
                  </Box>
                </>
              ))
            ) : (
              <Typography textAlign="center" color="text.secondary">
                No districts found.
              </Typography>
            )}
          </Box>
        </AnalyticsCard>
      </Modal>
    </Box>
  );
};

export default BankingSection;
